import React from 'react';
import { motion } from 'framer-motion';

const TeamSection = () => {
  return (
    <div className="bg-gradient-to-b from-white to-blue-50 py-8 sm:py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Author Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="relative mb-16 sm:mb-20 lg:mb-32"
        >
          {/* Decorative Elements - Hidden on mobile for better space utilization */}
          <motion.div
            initial={{ rotate: -10, scale: 0.9 }}
            whileInView={{ rotate: 0, scale: 1 }}
            className="hidden sm:block absolute -top-10 -left-10 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-yellow-200 rounded-full opacity-50 z-0"
          />
          <motion.div
            initial={{ rotate: 10, scale: 0.9 }}
            whileInView={{ rotate: 0, scale: 1 }}
            className="hidden sm:block absolute top-20 -right-10 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-200 rounded-full opacity-50 z-0"
          />

          {/* Content Container */}
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl overflow-hidden z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Image Column */}
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                className="lg:col-span-5 relative"
              >
                <div className="h-64 sm:h-80 md:h-96 lg:min-h-[400px] relative overflow-hidden">
                  <img 
                    src="/images/carter1.jpg" 
                    alt="Carter Case" 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-transparent" />
                </div>
              </motion.div>

              {/* Text Column */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                className="lg:col-span-7 p-6 sm:p-8 lg:p-12 flex flex-col justify-center"
              >
                <h2 className="text-[#4169E1] text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <span>Meet Author</span>
                  <span className="relative inline-block">
                    Carter Case
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      className="absolute -bottom-2 left-0 h-2 sm:h-3 bg-yellow-200 -z-10"
                    />
                  </span>
                </h2>
                <div className="space-y-3 sm:space-y-4 text-slate-700 text-base sm:text-lg relative">
                  <p>
                    Meet the creative mind behind the delightful tales of "Ned and Nancy" – Carter Case. 
                    With a passion for storytelling and a knack for crafting humorous and mysterious adventures, 
                    Carter has captivated the hearts of young readers and their families.
                  </p>
                  <p className="hidden sm:block">
                    In his mid-30s, Carter Case brings a fresh perspective to the world of children's literature. 
                    Drawing inspiration from his own childhood adventures and a vivid imagination, 
                    he weaves engaging narratives that resonate with both kids and adults alike.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Illustrator Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="relative"
        >
          {/* Decorative Elements - Hidden on mobile */}
          <motion.div
            initial={{ rotate: 10, scale: 0.9 }}
            whileInView={{ rotate: 0, scale: 1 }}
            className="hidden sm:block absolute -top-10 -right-10 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-purple-200 rounded-full opacity-50 z-0"
          />
          <motion.div
            initial={{ rotate: -10, scale: 0.9 }}
            whileInView={{ rotate: 0, scale: 1 }}
            className="hidden sm:block absolute top-20 -left-10 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-pink-200 rounded-full opacity-50 z-0"
          />

          {/* Content Container */}
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl overflow-hidden z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Text Column */}
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                className="lg:col-span-7 p-6 sm:p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1"
              >
                <h2 className="text-[#4169E1] text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <span>Meet Illustrator</span>
                  <span className="relative inline-block">
                    Rasa
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      className="absolute -bottom-2 left-0 h-2 sm:h-3 bg-purple-200 -z-10"
                    />
                  </span>
                </h2>
                <div className="space-y-3 sm:space-y-4 text-slate-700 text-base sm:text-lg">
                  <p>
                    Rasa's illustrations are celebrated for their ability to capture the imagination of 
                    both young and old readers. Her work on the Ned and Nancy series has been instrumental 
                    in bringing their adventures to life through vibrant and engaging artwork.
                  </p>
                  <p className="hidden sm:block">
                    With a distinctive style that blends whimsy and detail, Rasa creates illustrations 
                    that perfectly complement the stories' mysterious and humorous nature. Each page 
                    becomes a visual journey that invites readers to explore and discover hidden details.
                  </p>
                </div>
              </motion.div>

              {/* Image Column */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                className="lg:col-span-5 relative order-1 lg:order-2"
              >
                <div className="h-64 sm:h-80 md:h-96 lg:min-h-[400px] relative overflow-hidden">
                  <img 
                    src="/images/rasa.jpg" 
                    alt="Rasa" 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-purple-500/20 to-transparent" />
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeamSection;